import * as i0 from '@angular/core';
import { Injectable, NgModule, EventEmitter, Optional, Component } from '@angular/core';
import * as i1$1 from '@seco/core';
import { ReefAppModule, HTTP_CONFIG_TOKEN, EquipmentsService, AnalyticsService, Bear1AXMLRSFService, QualityMonitorService, CustomizationOrchestratorService, UserPreferencesService } from '@seco/core';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, HttpHeaders } from '@angular/common/http';
import * as i4 from '@angular/forms';
import { UntypedFormGroup, UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { first } from 'rxjs/operators';
import { Subject, firstValueFrom } from 'rxjs';
import * as i1$2 from 'bear-1axml';
import { Bear1AXMLService } from 'bear-1axml';

class HttpKassetteInterceptor {
    constructor() {
        this.actionsCounter = {};
        // TODO - Remove this redirect when migrating to Playwright
        // as we will be able to use Playwright browser with browser proxy setup automatically
        // remove 'https://test.qa.amanda-chatbot.api.amadeus.com' after changes are mitigated to training for ai fare rules
        this.redirectUrls = [
            'https://pdt.accounts.amadeus.com',
            'https://uat.accounts.amadeus.com',
            'https://uat.servicehub.amadeus.com',
            'https://pdt.accounts.sellingplatformconnect.amadeus.com',
            'https://test.qa.amanda-chatbot.api.amadeus.com',
            'https://test.training.amanda-chatbot.api.amadeus.com'
        ];
        this.excludedUrls = [];
    }
    // Mock server logic in order to be able to record multiple same calls.
    intercept(request, next) {
        if (!this.isUrlIncluded(request.url, this.excludedUrls)) {
            let urlToGo = request.url;
            const headers = {};
            if (this.isUrlIncluded(request.url, this.redirectUrls)) {
                urlToGo = `/kassette-external-mock${new URL(request.url).pathname}`;
                headers['Kassette-RedirectUrl'] = request.url;
            }
            const actionName = new URL(request.url, window.location.origin).pathname;
            let actionCount = this.actionsCounter[actionName];
            if (actionCount) {
                ++this.actionsCounter[actionName];
                actionCount = this.actionsCounter[actionName];
            }
            else {
                actionCount = 1;
                this.actionsCounter[actionName] = actionCount;
            }
            headers['Kassette-RequestId'] = actionCount.toString();
            request = request.clone({
                url: urlToGo,
                setHeaders: headers
            });
        }
        return next.handle(request);
    }
    isUrlIncluded(url, list) {
        return !!list.find((urlInList) => url.startsWith(urlInList));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: HttpKassetteInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: HttpKassetteInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: HttpKassetteInterceptor, decorators: [{
            type: Injectable
        }] });

const defaultConfig = {
    siteCode: '0SLW0SLW',
    language: 'GB',
    baseUrl: '/app_sell2.0',
    jSessionId: ''
};
/**
 * Configures the stand-alone HTTP client. Configure the HTTP client in your standalone module:
 *
 * ```
 * @NgModule({
 *   imports: [
 *     HttpModule.forRoot({ siteCode: '0SLW0SLW', language: 'GB' }),
 *   ]
 * })
 * export class StandaloneModule {}
 * ```
 */
class HttpModule {
    static forRoot(config = {}, useMockServer = false) {
        return {
            ngModule: HttpModule,
            providers: [
                ReefAppModule.forRoot().providers,
                {
                    provide: HTTP_CONFIG_TOKEN,
                    useValue: { ...defaultConfig, ...config }
                },
                ...(useMockServer
                    ? [
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: HttpKassetteInterceptor,
                            multi: true
                        }
                    ]
                    : []),
                EquipmentsService
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: HttpModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.12", ngImport: i0, type: HttpModule, imports: [CommonModule, ReefAppModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: HttpModule, providers: [provideHttpClient(withInterceptorsFromDi())], imports: [CommonModule, ReefAppModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: HttpModule, decorators: [{
            type: NgModule,
            args: [{ imports: [CommonModule, ReefAppModule], providers: [provideHttpClient(withInterceptorsFromDi())] }]
        }] });

/**
 * Implementation of Analytics Service for use in Standalone.
 *
 * This implementation tracks actions by executing the following HTTP request:
 * POST /save-frontoffice-data/v1
 *
 */
class AnalyticsServiceImpl extends AnalyticsService {
    constructor(http) {
        super();
        this.http = http;
        this.BASE_URL = 'save-frontoffice-data/v1';
        this.GET_PARAM_KEY = 'NG_ANALYTICS';
    }
    track(args) {
        const params = this.getData(args);
        this.http.post(this.BASE_URL, params).subscribe();
    }
    stopTimer(_action, _actionValue) {
        // Empty function
    }
    getData(args) {
        return {
            data: [
                {
                    category: this.GET_PARAM_KEY,
                    analytics: {
                        category: this.GET_PARAM_KEY,
                        deltaTime: args.deltaTime ? args.deltaTime : 0,
                        action_name: args.action,
                        action_value: args.action_value,
                        freeText: args.freeText ? args.freeText : 'none',
                        context: args.context ? args.context : 'none'
                    }
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsServiceImpl, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsServiceImpl }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsServiceImpl, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

/**
 * Configures the stand-alone Analytics service
 * To have standalone analytics in your standalone:
 *
 * ```
 * @NgModule({
 *   imports: [
 *     AnalyticsModule.forRoot(),
 *   ]
 * })
 * export class StandaloneModule {}
 * ```
 */
class AnalyticsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsModule, imports: [HttpModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsModule, providers: [{ provide: AnalyticsService, useClass: AnalyticsServiceImpl }], imports: [HttpModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpModule],
                    providers: [{ provide: AnalyticsService, useClass: AnalyticsServiceImpl }]
                }]
        }] });

/**
 * An analytics mock that doesnt use network
 */
class AnalyticsServiceMock {
    track(args) {
        window.console.log('Analytics Event Logged', args);
    }
    stopTimer(_action, _actionValue) {
        // Empty function
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsServiceMock, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsServiceMock }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AnalyticsServiceMock, decorators: [{
            type: Injectable
        }] });

class AshServiceMock {
    constructor() {
        this.isAshLinkActivated = false;
        this.SECO_ASH_ERROR_MAPPING = {
            '5045': 100001,
            '99': 100004,
            '8703': 24335,
            '100': 2467264,
            '9999': 9898
        };
    }
    getAshApiUrl() {
        return 'https://testAshUrl/';
    }
    sendContentViewerData(url, title, fullContentUrl) { }
    getAshId(errorId) {
        return this.SECO_ASH_ERROR_MAPPING[errorId];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AshServiceMock, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AshServiceMock }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AshServiceMock, decorators: [{
            type: Injectable
        }] });

class Command {
    constructor(command) {
        // Command status: pending, failure, success
        this.status = 'PENDING';
        this.command = command;
    }
    /**
     * Returns the query to be sent to the secolean APF+ endpoint
     * used to run the cryptic command
     */
    getQuery() {
        return {
            messageAction: {
                messageFunctionDetails: {
                    messageFunction: 'M'
                }
            },
            longTextString: {
                textStringDetails: this.command
            }
        };
    }
    /**
     * Update the properties in this object based on the response of the server
     * @param response cryptic command object returned by the secolean cryptic endpoint
     */
    handleResponse(response) {
        this.response = response;
        if (response) {
            this.status = 'SUCCESS';
        }
        else {
            this.status = 'FAILURE';
        }
    }
}

/** @dynamic */
class Credentials {
}

/* eslint-disable no-console */
class ClpService {
    constructor(http) {
        this.http = http;
        this.CLP_APP_ID = 'SECO';
    }
    init(config, callback) {
        this.utils = new window.clp.Utils();
        this.utils.post(this.wrapPost.bind(this));
        this.utils.get(this.wrapGet.bind(this));
        this.getters = new window.clp.login.Getters();
        this.callbacks = new window.clp.login.Callbacks();
        window.clp.init(this.CLP_APP_ID, config, this.utils, callback);
    }
    login() {
        window.clp.login.init(this.getters, this.callbacks);
        window.clp.login.login();
    }
    setCallbacks(onSuccess, onError) {
        this.callbacks.onSuccess(onSuccess);
        this.callbacks.onError(onError);
    }
    setGetters(credentials) {
        this.getters.getDutyCode(() => '');
        console.log('CLP init');
        this.getters.getLogin(() => credentials.userId);
        this.getters.getPassword(() => credentials.password);
        this.getters.getOfficeId(() => credentials.officeId);
        this.getters.getOneTimePassword(() => '');
    }
    wrapGet(url, onSuccess, onError) {
        this.http.get(url).subscribe({ next: onSuccess, error: onError });
    }
    wrapPost(url, body, onSuccess, onError) {
        const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
        this.http.post(url, body, { headers, withCredentials: true }).subscribe({ next: onSuccess, error: onError });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: ClpService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: ClpService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: ClpService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

class AutoLoginService {
    constructor(zone, httpService, clpService, reefGw, httpConfigService) {
        this.zone = zone;
        this.httpService = httpService;
        this.clpService = clpService;
        this.reefGw = reefGw;
        this.httpConfigService = httpConfigService;
        this.jSessionId$ = new Subject();
        this.loginSuccess = new EventEmitter();
        this.loginError = new EventEmitter();
        this.BUID_CONFIG_TOKEN = 'buildConfigTokenAction';
        this.SIGN_IN_ACTION = 'UMSignInByAccessToken';
        this.credentials = Credentials;
    }
    autoLogin(formInputs) {
        if (!formInputs || !formInputs.userId || !formInputs.officeId || !formInputs.password) {
            this.credentials = Credentials;
        }
        else {
            this.credentials = formInputs;
        }
        const ENV = this.httpConfigService.getConfig()?.environment || 'pdt';
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = `https://${ENV}.accounts.amadeus.com/LoginService/embedUiLess?v=1.1`;
        script.async = true;
        window.onClpApiLoaded = this.initClp.bind(this);
        document.getElementsByTagName('head')[0].appendChild(script);
    }
    onClpSuccess(nonce, accessToken, idToken) {
        const loginData = {
            ACCESS_TOKEN: accessToken,
            ID_TOKEN: idToken,
            NONCE: nonce
        };
        this.httpService.postDac(this.SIGN_IN_ACTION, loginData).subscribe((res) => {
            if (typeof res === 'object' && res != null && 'JsessionId' in res && typeof res.JsessionId === 'string') {
                const config = this.httpConfigService.getConfig();
                if (config) {
                    this.httpConfigService.initConfig({
                        ...config,
                        jSessionId: res.JsessionId
                    });
                }
                else {
                    throw new Error('HttpService config not found');
                }
                this.jSessionId$.next(res.JsessionId);
                this.reefGw?.loginInThroughJCP().subscribe();
                this.loginSuccess.emit();
            }
        });
    }
    initClp() {
        this.zone.run(() => {
            this.httpService.postDac(this.BUID_CONFIG_TOKEN, {}).subscribe((res) => {
                const config = { configToken: res.success.configToken, nonce: res.success.nonce };
                this.clpService.init(config, this.loginOnClp.bind(this, res.success.nonce));
            });
        });
    }
    loginOnClp(nonce) {
        this.clpService.setCallbacks(this.onClpSuccess.bind(this, nonce), () => {
            this.loginError.emit();
        });
        if (!this.credentials.userId || !this.credentials.officeId || !this.credentials.password) {
            this.loginError.emit();
            throw new Error('Credentials are undefined, modify credentials.ts');
        }
        this.clpService.setGetters(this.credentials);
        this.clpService.login();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AutoLoginService, deps: [{ token: i0.NgZone }, { token: i1$1.HttpService }, { token: ClpService }, { token: i1$1.ReefGatewayService, optional: true }, { token: i1$1.HttpConfigService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AutoLoginService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AutoLoginService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: i1$1.HttpService }, { type: ClpService }, { type: i1$1.ReefGatewayService, decorators: [{
                    type: Optional
                }] }, { type: i1$1.HttpConfigService }] });

/**
 * Cryptic Command service that can be used to initialize your standalone module.
 * Example usage:
 *
 * ```
 * export class StandaloneComponent implements OnInit {
 *   constructor(
 *     private readonly crypticService: CrypticService
 *   ) {}
 *
 *   ngOnInit() {
 *     const command = new Command('NM1SMITH/JOHN');
 *     this.crypticService.executeCommand(command).then(() => this.doSomething());
 *   }
 * }
 * ```
 */
class CrypticService {
    constructor(bear) {
        this.bear = bear;
    }
    /**
     * Execute multiple cryptic commands sequentially.
     * Every Command object is updated with the status and response.
     * @param commands array of commands to be executed sequentially
     */
    executeCommands(commands) {
        return commands.reduce((prev, curr) => prev.then((res) => {
            curr.status = 'INPROGRESS';
            return this.executeCommand(curr).then((result) => {
                curr.handleResponse(result);
                res.push(result);
                return res;
            });
        }), Promise.resolve([]));
    }
    /**
     * Execute a single cryptic command.
     * The provided Command object will be updated with the status and response.
     *
     * @param command command to be executed
     */
    executeCommand(command) {
        command.status = 'INPROGRESS';
        return firstValueFrom(this.bear.commandCryptic(command.getQuery()));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: CrypticService, deps: [{ token: i1$2.Bear1AXMLService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: CrypticService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: CrypticService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1$2.Bear1AXMLService }] });

class AutoLoginComponent {
    constructor(login, crypticService) {
        this.login = login;
        this.crypticService = crypticService;
        // true when authentication in progress
        this.authenticating = false;
        // true when successfully authenticated
        this.authenticated = false;
        this.sessionId = '';
        this.loginForm = new UntypedFormGroup({
            userId: new UntypedFormControl(),
            officeId: new UntypedFormControl(),
            password: new UntypedFormControl()
        });
    }
    async doLogin(formInputs) {
        this.login.jSessionId$.pipe(first()).subscribe((sessionId) => {
            this.authenticated = !!sessionId;
            this.authenticating = false;
            this.sessionId = sessionId;
        });
        this.authenticating = true;
        this.login.autoLogin(formInputs);
    }
    dump() {
        const dumpCmd = new Command('$$dump');
        this.crypticService.executeCommand(dumpCmd).then((ret) => {
            window.console.log(ret.longTextString?.textStringDetails);
        });
        window.console.log(`JSessionID: ${this.sessionId}`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AutoLoginComponent, deps: [{ token: AutoLoginService }, { token: CrypticService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.12", type: AutoLoginComponent, selector: "ama-autologin", ngImport: i0, template: "<form *ngIf=\"!authenticated\" class=\"container mt-4\" [formGroup]=\"loginForm\" (ngSubmit)=\"doLogin(loginForm.value)\">\n  <div class=\"row\">\n    <div class=\"col\">\n      <input type=\"text\" formControlName=\"userId\" class=\"form-control\" placeholder=\"Username\" />\n    </div>\n    <div class=\"col\">\n      <input type=\"text\" formControlName=\"officeId\" class=\"form-control\" placeholder=\"Office Id\" />\n    </div>\n    <div class=\"col\">\n      <input type=\"password\" formControlName=\"password\" class=\"form-control\" placeholder=\"Password\" />\n    </div>\n    <div>\n      <button class=\"btn btn-primary\">Login</button>\n    </div>\n  </div>\n</form>\n<span *ngIf=\"authenticated\" id=\"jsessionId\">\n  {{ login.credentials.userId }} &#64; {{ login.credentials.officeId }}\n  <br />\n  <button class=\"form-control btn btn-secondary\" (click)=\"dump()\">$$DUMP</button>\n</span>\n", dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i4.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i4.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i4.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i4.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AutoLoginComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ama-autologin', template: "<form *ngIf=\"!authenticated\" class=\"container mt-4\" [formGroup]=\"loginForm\" (ngSubmit)=\"doLogin(loginForm.value)\">\n  <div class=\"row\">\n    <div class=\"col\">\n      <input type=\"text\" formControlName=\"userId\" class=\"form-control\" placeholder=\"Username\" />\n    </div>\n    <div class=\"col\">\n      <input type=\"text\" formControlName=\"officeId\" class=\"form-control\" placeholder=\"Office Id\" />\n    </div>\n    <div class=\"col\">\n      <input type=\"password\" formControlName=\"password\" class=\"form-control\" placeholder=\"Password\" />\n    </div>\n    <div>\n      <button class=\"btn btn-primary\">Login</button>\n    </div>\n  </div>\n</form>\n<span *ngIf=\"authenticated\" id=\"jsessionId\">\n  {{ login.credentials.userId }} &#64; {{ login.credentials.officeId }}\n  <br />\n  <button class=\"form-control btn btn-secondary\" (click)=\"dump()\">$$DUMP</button>\n</span>\n" }]
        }], ctorParameters: () => [{ type: AutoLoginService }, { type: CrypticService }] });

class AutoLoginModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AutoLoginModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.12", ngImport: i0, type: AutoLoginModule, declarations: [AutoLoginComponent], imports: [CommonModule, HttpModule, ReactiveFormsModule], exports: [AutoLoginComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AutoLoginModule, providers: [AutoLoginService, ClpService, provideHttpClient(withInterceptorsFromDi())], imports: [CommonModule, HttpModule, ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: AutoLoginModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AutoLoginComponent],
                    exports: [AutoLoginComponent],
                    imports: [CommonModule, HttpModule, ReactiveFormsModule],
                    providers: [AutoLoginService, ClpService, provideHttpClient(withInterceptorsFromDi())]
                }]
        }] });

/**
 * Configures the stand-alone Bear service
 * To have standalone Bear in your standalone:
 *
 * ```
 * @NgModule({
 *   imports: [
 *     BearModule,
 *   ]
 * })
 * export class BearModule {}
 * ```
 */
class BearModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: BearModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.12", ngImport: i0, type: BearModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: BearModule, providers: [{ provide: Bear1AXMLService, useClass: Bear1AXMLRSFService }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: BearModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [{ provide: Bear1AXMLService, useClass: Bear1AXMLRSFService }]
                }]
        }] });

class MockClpLoaderService {
    loadClpAPI(clpBaseUrl) {
        import('@reef-dev/clp-mock').then(() => {
            window.onClpApiLoaded();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: MockClpLoaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: MockClpLoaderService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: MockClpLoaderService, decorators: [{
            type: Injectable
        }] });

class DevUtilsComponent {
    constructor(login) {
        this.login = login;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: DevUtilsComponent, deps: [{ token: AutoLoginService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.12", type: DevUtilsComponent, selector: "ama-ng-dev-utils", ngImport: i0, template: "<p>\n  dev-utils works!\n</p>\n\n<button (click)=\"login.autoLogin()\">Login on CLP</button>\n\n<p>JsessionId: {{ login.jSessionId$ | async }}</p>\n", dependencies: [{ kind: "pipe", type: i3.AsyncPipe, name: "async" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: DevUtilsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ama-ng-dev-utils', template: "<p>\n  dev-utils works!\n</p>\n\n<button (click)=\"login.autoLogin()\">Login on CLP</button>\n\n<p>JsessionId: {{ login.jSessionId$ | async }}</p>\n" }]
        }], ctorParameters: () => [{ type: AutoLoginService }] });

class DevUtilsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: DevUtilsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.12", ngImport: i0, type: DevUtilsModule, declarations: [DevUtilsComponent], imports: [CommonModule, HttpModule, AutoLoginModule, AnalyticsModule, BearModule], exports: [DevUtilsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: DevUtilsModule, imports: [CommonModule, HttpModule.forRoot(), AutoLoginModule, AnalyticsModule, BearModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: DevUtilsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, HttpModule.forRoot(), AutoLoginModule, AnalyticsModule, BearModule],
                    declarations: [DevUtilsComponent],
                    exports: [DevUtilsComponent]
                }]
        }] });

/**
 * Implementation of Quality Monitor Service for use in Standalone.
 *
 * This implementation always executes the successCallback with mock data
 *
 */
class QualityMonitorServiceImpl extends QualityMonitorService {
    constructor() {
        super();
    }
    executeQM(command, origin, successCallback, errorCallback) {
        successCallback();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: QualityMonitorServiceImpl, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: QualityMonitorServiceImpl }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: QualityMonitorServiceImpl, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

class CustomizationOrchestratorServiceImpl extends CustomizationOrchestratorService {
    preProcessGraphic(command, context) {
        if (context && context.onContinueCallback) {
            context.onContinueCallback();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: CustomizationOrchestratorServiceImpl, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: CustomizationOrchestratorServiceImpl }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: CustomizationOrchestratorServiceImpl, decorators: [{
            type: Injectable
        }] });

const USER_PREFERENCES_MOCK = Object.freeze({
    userAlias: 'lssuser',
    officeList: ['NCE6X0980', 'NCE1A0950'],
    currentOffice: 'NCE1A0950',
    loginOffice: 'NCE1A0950'
});
class UserPreferencesServiceMock extends UserPreferencesService {
    constructor() {
        super();
        this.init(USER_PREFERENCES_MOCK);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: UserPreferencesServiceMock, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: UserPreferencesServiceMock }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.12", ngImport: i0, type: UserPreferencesServiceMock, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsModule, AnalyticsServiceImpl, AnalyticsServiceMock, AshServiceMock, AutoLoginComponent, AutoLoginModule, AutoLoginService, BearModule, Command, CrypticService, CustomizationOrchestratorServiceImpl, DevUtilsComponent, DevUtilsModule, HttpModule, MockClpLoaderService, QualityMonitorServiceImpl, USER_PREFERENCES_MOCK, UserPreferencesServiceMock };
